import {
  DataGridPro,
  DataGridProProps,
  GridColDef,
  GridRowSelectionModel,
  GridValidRowModel,
} from '@mui/x-data-grid-pro'
import cn from 'classnames'
import { base_colorContentBorder } from 'ext-ui/style/colors'
import { spacing } from 'ext-ui/style/spacing'
import { LocationState } from 'history'
import queryString from 'query-string'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import LoadingCell from 'src/components/DataGrid/cells/LoadingCell'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import TaskItem from 'src/components/TaskItem/TaskItem'
import { useTranslate } from 'src/localization/Trns'
import { TransactionListModel } from 'src/models/transaction.models'
import { activityUrl } from 'src/navigation/urls'
import { ActivityContext } from 'src/page-components/Activity/Activity'
import { useFetchTasks } from 'src/queries/activity.queries'
import { selectLocationFromState } from 'src/redux/selectors/router.selectors'

import style, { NoTasksMessageStyle } from './TasksList.style'

interface TaskDataGridRowModel extends GridValidRowModel {
  task: TransactionListModel
  index: number
}

interface LoadingRowModel extends GridValidRowModel {}

type TaskDataGridColumnType = Array<
  GridColDef<TaskDataGridRowModel | LoadingRowModel>
>

const TasksList = (): React.ReactElement => {
  const translate = useTranslate()

  const {
    data,
    fetchNextPage,
    isLoading,
    isFetching,
    isFetchingNextPage,
    isFetched,
    complete,
  } = useFetchTasks()

  // Sets row selection when the component mounts
  // so that the first task is visually selected
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>({ type: 'include', ids: new Set() })

  const history = useHistory<LocationState>()
  const location = useSelector(selectLocationFromState)

  const { setTasksComplete } = useContext(ActivityContext)

  useEffect(() => {
    if (location.pathname !== activityUrl()) {
      return
    }

    const params = queryString.parse(location.search)
    if (params.tab !== 'tasks') {
      return
    }
    if (params.id === undefined && data?.transactions?.[0]?.id !== undefined) {
      history.replace({
        pathname: activityUrl(),
        search: `?tab=tasks&id=${data.transactions[0].id}`,
      })
    }

    if (typeof params.id === 'string') {
      setRowSelectionModel({
        type: 'include',
        ids: new Set([params.id]),
      })
    }
  }, [location.search, data?.transactions?.[0]?.id])

  useEffect(() => {
    if (!isFetched) return
    const taskCount = data?.transactions?.length ?? 0
    if (taskCount === 0) {
      setTasksComplete(true)
    }
  }, [isFetched, data, setTasksComplete])

  const NoTasksMessage = (): React.ReactElement => (
    <div css={NoTasksMessageStyle}>
      {translate('No tasks on your plate. Enjoy the moment!')}
    </div>
  )

  const loadingColumns: TaskDataGridColumnType = [
    {
      field: 'task',
      headerName: translate('Task'),
      hideable: false,
      sortable: false,
      disableColumnMenu: true,
      flex: 1,
      renderCell: (params): React.ReactElement => {
        return <LoadingCell params={params} />
      },
      renderHeader: () => <></>,
    },
  ]

  const loadingRows = Array.from({ length: 7 }, (_, i) => ({
    id: `loading-${i}`,
  }))

  const columns: TaskDataGridColumnType = [
    {
      field: 'task',
      headerName: translate('Task'),
      hideable: false,
      sortable: false,
      disableColumnMenu: true,
      flex: 1,
      renderCell: (params): React.ReactElement => {
        const task = params.row.task

        return <TaskItem key={task.id} task={task} />
      },
      renderHeader: () => <></>,
    },
  ]

  const columnsSafe = useMemo(
    () =>
      columns.filter(
        (column) => column !== undefined,
      ) as TaskDataGridColumnType,
    [columns],
  )

  const rows = (data?.transactions ?? []).map((task, i) => {
    return {
      id: task.id,
      task: task,
      index: i,
    }
  })

  // handle infinite scroll
  const handleOnRowsScrollEnd = useCallback<
    NonNullable<DataGridProProps['onRowsScrollEnd']>
  >((): void => {
    if (!isFetching && !complete) {
      fetchNextPage()
    }
  }, [fetchNextPage, isFetching, complete])

  return (
    <div className={cn('TasksList')} css={style}>
      <DataGridPro
        rows={isLoading ? loadingRows : rows}
        columns={isLoading ? loadingColumns : columnsSafe}
        columnHeaderHeight={0}
        rowHeight={80}
        onRowsScrollEnd={handleOnRowsScrollEnd}
        rowSelectionModel={rowSelectionModel}
        sx={{
          border: 'none', // Remove border
          borderRadius: 0, // Remove border radius
          '& .MuiDataGrid-cell': {
            border: 'none',
          },
          // Add a partial separator line between rows
          '& .MuiDataGrid-row': {
            position: 'relative',
          },
          '& .MuiDataGrid-row:not(:first-of-type)::after': {
            content: '""',
            position: 'absolute',
            top: '-1px',
            left: spacing(5),
            right: spacing(5),
            height: '1px',
            background: base_colorContentBorder,
          },
          '.MuiDataGrid-cell:focus': {
            outline: 'none', // Remove focus outline
          },
          // Lower the scrollbar so it is below modals
          '.MuiDataGrid-scrollbar': {
            zIndex: 1,
          },
        }}
        loading={isFetchingNextPage}
        slots={{
          columnHeaders: () => null, // Removes header render
          loadingOverlay: () => <LoadingBackgroundPage />,
          noResultsOverlay: () => <NoTasksMessage />,
          noRowsOverlay: () => <NoTasksMessage />,
        }}
        hideFooter
        onRowClick={(params): void => {
          const task = params.row.task
          history.replace({
            pathname: activityUrl(),
            search: `?tab=tasks&id=${task.id}`,
          })
        }}
      />
    </div>
  )
}

export default TasksList
